import { useTranslation } from 'react-i18next';
import { BadgeCheck, Check, Lock, RefreshCw, Sparkles, ZoomIn, type LucideIcon } from 'lucide-react';
import { PromoImg } from './PromoImg';
import { SectionHeading } from './SectionHeading';
import { APPROVED_DRAWING, PROOF_REDRAW_ROUNDS } from './promo';

const STEPS: Array<{ key: string; icon: LucideIcon }> = [
  { key: 'draw', icon: Sparkles },
  { key: 'look', icon: ZoomIn },
  { key: 'redraw', icon: RefreshCw },
  { key: 'approve', icon: BadgeCheck },
];

/** The book page's own answer buttons (StorybookProof), drawn as a still: this is a picture of the check, not the check. */
const OPTION =
  'inline-flex min-h-11 items-center justify-center gap-1.5 rounded-full border px-3 py-2 text-center text-sm font-semibold leading-tight [&_svg]:h-4 [&_svg]:w-4 [&_svg]:shrink-0';

/**
 * "You approve every picture": after purchase every picture is drawn and the
 * parent checks each one (redraw or approve) before anything is made. The
 * mock is one card as the real check (StorybookProof) shows it: the approved
 * sample cover with its zoom badge, its label, and the two answers, in the
 * book page's own labels (storybookPage.book.proof.*). Faces are checked by
 * zooming into the picture, as the real check does; it shows no face
 * close-ups of its own, so neither does this.
 */
export function ApproveSection({ print }: { print: boolean }) {
  const { t } = useTranslation();
  const body = (key: string) =>
    t(`storybookPage.approve.steps.${key}.${key === 'approve' && !print ? 'bodyPdf' : 'body'}`, { rounds: PROOF_REDRAW_ROUNDS });
  return (
    <section
      id="approve"
      className="scroll-mt-20 bg-[linear-gradient(180deg,transparent,hsl(45_95%_94%)_22%,hsl(45_95%_94%)_78%,transparent)] py-14 sm:py-20"
      aria-labelledby="storybook-approve-title"
    >
      <div className="container mx-auto grid max-w-5xl items-center gap-10 px-4 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] lg:gap-14">
        <div className="lg:order-2">
          <SectionHeading
            id="storybook-approve-title"
            align="start"
            className="mb-6"
            eyebrow={t('storybookPage.approve.eyebrow')}
            title={t('storybookPage.approve.title')}
            subtitle={t('storybookPage.approve.subtitle')}
          />
          <ol className="mx-auto max-w-md space-y-4 lg:mx-0">
            {STEPS.map((step) => (
              <li key={step.key} className="flex items-start gap-3">
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-card text-primary shadow-sm ring-1 ring-border">
                  <step.icon className="h-4 w-4" aria-hidden="true" />
                </span>
                <span>
                  <span className="block font-semibold">{t(`storybookPage.approve.steps.${step.key}.title`)}</span>
                  <span className="mt-0.5 block text-sm leading-relaxed text-muted-foreground">
                    {body(step.key)}
                  </span>
                </span>
              </li>
            ))}
          </ol>
        </div>

        <figure className="glass-card mx-auto w-full max-w-md p-4 sm:p-5 lg:order-1 lg:max-w-none">
          <span className="inline-block rounded-full bg-accent px-3 py-1 text-xs font-bold uppercase tracking-wider text-accent-foreground">
            {t('storybookPage.book.proof.badge')}
          </span>
          <p className="storybook-display mt-2 text-lg font-semibold leading-snug">{t('storybookPage.approve.visual.question')}</p>

          <div className="mt-3 rounded-3xl border border-emerald-600/60 bg-card p-3 shadow-sm sm:p-4">
            <div className="relative overflow-hidden rounded-2xl bg-[#cde8f2]">
              <PromoImg
                image={APPROVED_DRAWING}
                sizes="(min-width: 1024px) 400px, (min-width: 480px) 380px, calc(100vw - 5.5rem)"
                className="aspect-square w-full object-cover"
              />
              <span aria-hidden="true" className="absolute bottom-2 right-2 flex h-9 w-9 items-center justify-center rounded-full bg-black/60 text-white">
                <ZoomIn className="h-4 w-4" />
              </span>
            </div>

            <p className="mt-3">
              <span className="block text-base font-semibold">{t('storybookPage.book.proof.cover')}</span>
              {/* The real check's zoom viewer says the same (book.proof.large.description). */}
              <span className="mt-0.5 flex items-center gap-1.5 text-xs leading-snug text-muted-foreground">
                <ZoomIn className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                {t('storybookPage.book.proof.large.description')}
              </span>
            </p>

            <div className="mt-3 grid grid-cols-2 gap-2">
              <span className={`${OPTION} border-emerald-600 bg-emerald-50 text-emerald-950`}>
                <Check aria-hidden="true" />
                {t('storybookPage.book.proof.looksRight')}
              </span>
              <span className={`${OPTION} border-border bg-card text-foreground`}>
                <RefreshCw aria-hidden="true" />
                {t('storybookPage.book.proof.redrawThis')}
              </span>
            </div>
          </div>

          <p className="mt-4 flex items-center gap-2 rounded-2xl bg-muted px-3 py-2.5 text-sm font-semibold">
            <Lock className="h-4 w-4 shrink-0 text-primary" aria-hidden="true" />
            {t('storybookPage.approve.visual.lock')}
          </p>
          <figcaption className="mt-3 text-xs text-muted-foreground">{t('storybookPage.approve.visual.caption')}</figcaption>
        </figure>
      </div>
    </section>
  );
}
